import type {
  OrderDetail,
  ProductionOrderSheetFields,
  ProductionOrderSheetProduction,
} from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { formatCount } from '@/shared/lib';
import { detailListStyles as styles } from '@/shared/ui';
import { DestinationsTable } from './destinations-table';
import { LinesTable } from './lines-table';
import { PartiesTable } from './parties-table';
import { ProductionOrderSheetThumbnail } from './production-order-sheet-thumbnail';

interface ProductionOrderSheetDocumentProps {
  readonly order: OrderDetail;
  readonly production: ProductionOrderSheetProduction;
  readonly fields: ProductionOrderSheetFields;
  /** 발주일 — YYYY-MM-DD. */
  readonly issuedOn: string;
  /** 시안 이미지를 불러왔거나 실패했을 때 — 인쇄 시점을 잡는 데 쓴다. */
  readonly onThumbnailSettled?: () => void;
  /** 주어지면 "디자인 시안"에 썸네일 대신 이것을 그린다(면별 시안). */
  readonly designPreview?: ReactNode;
}

const SECTION_HEADING_CLASS = 'mb-2 text-sm font-semibold';

/**
 * 인쇄하는 발주서 한 장(A4) — 제목·발주번호/발주일·수신/발신·제작 내용·품목/단가·시안·납품처·요청 사항·바닥 문구.
 * 화면 틀(어드민 셸)과 무관한 순수 표시 문서이며, 인쇄 때는 그림자·둥근 모서리를 걷고 검정 글자로 낸다.
 */
export const ProductionOrderSheetDocument = ({
  order,
  production,
  fields,
  issuedOn,
  onThumbnailSettled,
  designPreview,
}: ProductionOrderSheetDocumentProps): ReactElement => {
  const { issuer } = order.quoteVersion;

  return (
    <article
      aria-label="발주서"
      className="mx-auto flex w-full max-w-[210mm] flex-col gap-6 rounded-md border bg-white p-10 text-black shadow-sm print:max-w-none print:gap-5 print:rounded-none print:border-0 print:p-0 print:shadow-none"
    >
      <header className="flex flex-col gap-3">
        <h1 className="text-center text-2xl font-bold tracking-widest">발주서</h1>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground print:text-black">발주번호</dt>
          <dd>{order.orderNumber}</dd>
          <dt className="text-muted-foreground print:text-black">발주일</dt>
          <dd>{issuedOn}</dd>
        </dl>
      </header>

      <section>
        <PartiesTable fields={fields} issuer={issuer} />
      </section>

      <section>
        <h2 className={SECTION_HEADING_CLASS}>1. 제작 내용</h2>
        <dl className={`${styles.list} print:text-black`}>
          <dt className={`${styles.term} print:text-black`}>상품</dt>
          <dd>{production.productName}</dd>
          <dt className={`${styles.term} print:text-black`}>색상</dt>
          <dd>{production.colorName}</dd>
          <dt className={`${styles.term} print:text-black`}>인쇄 방식</dt>
          <dd>{production.printMethodName ?? '인쇄 없음'}</dd>
          <dt className={`${styles.term} print:text-black`}>디자인명</dt>
          <dd>{production.designName ?? '—'}</dd>
          <dt className={`${styles.term} print:text-black`}>총 수량</dt>
          <dd>{formatCount(order.quantity)}</dd>
          <dt className={`${styles.term} print:text-black`}>납기 희망일</dt>
          <dd>{fields.dueDate ?? ''}</dd>
        </dl>
      </section>

      <section>
        <h2 className={SECTION_HEADING_CLASS}>2. 품목 및 단가</h2>
        <LinesTable lines={fields.lines} />
      </section>

      <section>
        <h2 className={SECTION_HEADING_CLASS}>3. 디자인 시안</h2>
        {designPreview !== undefined ? (
          designPreview
        ) : order.thumbnailUrl === null ? (
          <p className="text-sm">시안 이미지 없음</p>
        ) : (
          <ProductionOrderSheetThumbnail url={order.thumbnailUrl} onSettled={onThumbnailSettled} />
        )}
      </section>

      <section>
        <h2 className={SECTION_HEADING_CLASS}>4. 납품처</h2>
        <DestinationsTable destinations={order.destinations} />
      </section>

      <section>
        <h2 className={SECTION_HEADING_CLASS}>5. 요청 사항</h2>
        <p className="min-h-12 text-sm whitespace-pre-wrap">{fields.note}</p>
      </section>

      <footer className="border-t border-foreground/20 pt-4 text-center text-sm print:border-black">
        위와 같이 발주합니다. {issuer.companyName} (인)
      </footer>
    </article>
  );
};
